
import { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useState } from 'react';
import { FulfillmentColumn, Fulfillment, FulfillmentStage } from '@/types/fulfillment';
import { useUpdateProjectStatus } from './use-fulfillment-data';
import { useToast } from '@/hooks/errors/use-toast';

export const useFulfillmentDrag = (
  initialColumns: FulfillmentColumn[], 
  setColumns: React.Dispatch<React.SetStateAction<FulfillmentColumn[]>>
) => {
  const [activeProject, setActiveProject] = useState<Fulfillment | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { updateStatus } = useUpdateProjectStatus();
  const { toast } = useToast();

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const id = active.id as string;

    // Find the active project
    for (const column of initialColumns) {
      const foundProject = column.projects.find(proj => proj.id === id);
      if (foundProject) {
        setActiveProject(foundProject);
        break;
      }
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Find the source and destination columns
    const activeColumnId = findColumnByProjectId(activeId, initialColumns);
    const overColumnId = findColumnByProjectId(overId, initialColumns) || overId;

    if (!activeColumnId || activeColumnId === overColumnId) return;

    setColumns((prevColumns: FulfillmentColumn[]) => {
      // Get the project
      const activeColumn = prevColumns.find(col => col.id === activeColumnId);
      if (!activeColumn) return prevColumns;

      const activeProject = activeColumn.projects.find(p => p.id === activeId);
      if (!activeProject) return prevColumns;

      // Remove the project from the active column
      const updatedActiveColumn = {
        ...activeColumn,
        projects: activeColumn.projects.filter(p => p.id !== activeId)
      };

      // Add the project to the new column with updated status
      const updatedProject = { ...activeProject, status: overColumnId as FulfillmentStage };

      // Create the updated columns array
      return prevColumns.map(col => {
        if (col.id === activeColumnId) return updatedActiveColumn;
        if (col.id === overColumnId) {
          return {
            ...col,
            projects: [...col.projects, updatedProject]
          };
        }
        return col;
      });
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveProject(null);
    
    if (!over) return;
    
    const activeId = active.id as string;
    const overId = over.id as string;
    
    // If dropping on a column directly
    if (isColumnId(overId, initialColumns)) {
      try {
        setIsUpdating(true);
        console.log(`[DragEnd] Moving project ${activeId} to column ${overId}`);
        
        // Update the project status in the database
        await updateStatus(activeId, overId as FulfillmentStage);
        
        toast({
          title: "Status Updated",
          description: `Project moved to ${getColumnTitle(overId, initialColumns)}`,
          variant: "success",
        });
      } catch (error) {
        console.error('[DragEnd] Error updating status:', error);
        toast({
          title: "Update Failed",
          description: "Could not move the project. Please try again.",
          variant: "error",
        });
        
        // Revert the UI to match the database
        // This will be handled by the useQuery refetch
      } finally {
        setIsUpdating(false);
      }
      return;
    }
    
    // Reordering within the same column
    const activeColumnId = findColumnByProjectId(activeId, initialColumns);
    const overColumnId = findColumnByProjectId(overId, initialColumns);
    
    if (!activeColumnId || !overColumnId || activeColumnId !== overColumnId) return;
    
    setColumns((prevColumns: FulfillmentColumn[]) => {
      const column = prevColumns.find(col => col.id === activeColumnId);
      if (!column) return prevColumns;
      
      const oldIndex = column.projects.findIndex(p => p.id === activeId);
      const newIndex = column.projects.findIndex(p => p.id === overId);
      
      if (oldIndex === -1 || newIndex === -1) return prevColumns;
      
      const updatedColumn = {
        ...column,
        projects: arrayMove(column.projects, oldIndex, newIndex)
      };
      
      return prevColumns.map(col => col.id === activeColumnId ? updatedColumn : col);
    });
  };

  return {
    activeProject,
    isUpdating,
    handleDragStart,
    handleDragOver,
    handleDragEnd
  };
};

// Helper function to find which column a project belongs to
const findColumnByProjectId = (projectId: string, columns: FulfillmentColumn[]): string | undefined => {
  for (const column of columns) {
    if (column.projects.some(project => project.id === projectId)) {
      return column.id;
    }
  }
  return undefined;
};

// Helper function to check if an ID belongs to a column
const isColumnId = (id: string, columns: FulfillmentColumn[]): boolean => {
  return columns.some(column => column.id === id);
};

// Helper function to get a column title by ID
const getColumnTitle = (columnId: string, columns: FulfillmentColumn[]): string => {
  const column = columns.find(col => col.id === columnId);
  return column?.title || 'Unknown';
};
